import React from "react";

import clsx from "clsx";

import { type DraggableAttributes } from "@dnd-kit/core";
import { type SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { type CategoricalBreakdownConfig } from "@north-beam/nb-common";

import { BreakdownSelector } from "../../shared";
import { type TopLevelBreakdown } from "../../types";

interface Props {
  breakdownConfigs: CategoricalBreakdownConfig[];
  breakdown: TopLevelBreakdown;
  position: number;
  showPosition: boolean;
  onChange(value: TopLevelBreakdown): void;
  onDelete(value: TopLevelBreakdown): void;
}

export const BreakdownButtonActive = ({
  breakdownConfigs,
  breakdown,
  position,
  showPosition,
  onChange,
  onDelete,
}: Props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({
    id: breakdown.key,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const breakdownConfig = breakdownConfigs.find((e) => e.key === breakdown.key);

  if (!breakdownConfig) {
    return null;
  }

  return (
    <div
      ref={setNodeRef}
      className={clsx("group border-none bg-white", { "z-[999]": isDragging })}
      style={style}
    >
      <BreakdownSelector
        mode="breakdowns"
        showRemoveButton
        breakdownConfigs={breakdownConfigs}
        selectedBreakdowns={[breakdown]}
        buttonContentComponent={({ isOpen }) => (
          <Button
            text={breakdownConfig.name}
            position={position}
            showPosition={showPosition}
            isOpen={isOpen}
            isDragging={isDragging}
            isSorting={isSorting}
            attributes={attributes}
            listeners={listeners}
            setActivatorNodeRef={setActivatorNodeRef}
          />
        )}
        buttonClassName={clsx(
          "button sm secondary-button h-7 py-0",
          "hover:bg-cosmic-3 flex items-stretch border-none",
          {
            "shadow-sm bg-cosmic-3 border-cosmic": isDragging,
            "bg-cosmic-4 border-cosmic-4 hover:border-cosmic-3": !isDragging,
          },
        )}
        buttonActiveClassName={clsx(
          "button sm secondary-button h-7 py-0 border-cosmic text-white",
          "bg-cosmic flex items-stretch",
        )}
        onSelectionChange={(values) => onChange(values[0])}
        onDelete={onDelete}
      />
    </div>
  );
};

const Button = ({
  text,
  position,
  showPosition,
  isOpen,
  isDragging,
  isSorting,
  attributes,
  listeners,
  setActivatorNodeRef,
}: {
  text: string;
  position: number;
  showPosition: boolean;
  isOpen: boolean;
  isDragging: boolean;
  isSorting: boolean;
  attributes: DraggableAttributes;
  listeners?: SyntheticListenerMap;
  setActivatorNodeRef(el: HTMLElement | null): void;
}) => (
  <div className="flex items-center max-w-[60px]">
    {showPosition && (
      <div
        className={clsx("text-xxs mr-1.5 -ml-2.5")}
        onClick={(e) => {
          if (!isOpen) {
            e.stopPropagation();
          }
        }}
      >
        <span
          className={clsx(
            "w-4 h-4 rounded-full flex items-center justify-center",
            {
              "bg-cosmic text-white group-hover:hidden": !isOpen,
              "bg-white text-cosmic": isOpen,
              hidden: isDragging,
            },
          )}
        >
          {!isSorting && position}
        </span>
        <i
          ref={setActivatorNodeRef}
          className={clsx(
            "fa-regular fa-solid fa-grip-vertical w-4 h-4 cursor-grab",
            "flex items-center justify-center text-xs",
            {
              hidden: !isDragging,
              "block cursor-grabbing": isDragging,
              "group-hover:block": !isOpen,
            },
          )}
          {...attributes}
          {...listeners}
        ></i>
      </div>
    )}
    <div className="truncate">{text}</div>
  </div>
);
